import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiSettings } from 'react-icons/fi';
import toast from 'react-hot-toast';
import Header from '../components/layout/Header';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Avatar from '../components/ui/Avatar';
import Modal from '../components/ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import { isRhUser } from '../utils/roles';

const ROLE_LABELS: Record<string, string> = {
  rh: 'Responsable RH',
  collaborateur: 'Collaborateur',
  admin: 'Responsable RH',
  dirigeant: 'Responsable RH',
  manager: 'Responsable RH',
  salarie: 'Collaborateur',
};

const UserSettingsPage: React.FC = () => {
  const { user, resetOnboarding } = useAuth();
  const navigate = useNavigate();
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleResetOnboarding = async () => {
    setResetting(true);
    try {
      await resetOnboarding();
      toast.success('Introduction réinitialisée');
      setShowResetModal(false);
      navigate('/onboarding');
    } catch {
      toast.error('Impossible de réinitialiser l\'introduction');
    } finally {
      setResetting(false);
    }
  };

  if (!user) return null;

  return (
    <div>
      <Header title="Paramètres" subtitle="Gérez votre compte et vos préférences" />

      <div style={{ padding: '2rem', maxWidth: 720, margin: '0 auto' }}>
        <Card padding="1.5rem" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
            <Avatar name={`${user.first_name} ${user.last_name}`} size={56} src={user.avatar_url} />
            <div>
              <h2 style={{ margin: '0 0 0.25rem', fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                {user.first_name} {user.last_name}
              </h2>
              <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                {user.email}
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Rôle</span>
              <Badge variant="info">{ROLE_LABELS[user.role] || user.role}</Badge>
            </div>
            {isRhUser(user) && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Introduction Jul&apos;IA</span>
              <Badge variant={user.onboarding_completed ? 'success' : 'warning'}>
                {user.onboarding_completed ? 'Terminée' : 'À faire'}
              </Badge>
            </div>
            )}
          </div>
        </Card>

        {isRhUser(user) && (
        <Card padding="1.5rem" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.875rem', alignItems: 'flex-start' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 'var(--radius-lg)',
                background: 'var(--color-brand-light)',
                color: 'var(--color-brand)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FiRefreshCw size={18} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.35rem', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
                Refaire l&apos;introduction
              </h3>
              <p style={{ margin: '0 0 1rem', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                Relancez le parcours de découverte avec Julia pour revoir les étapes d&apos;accueil
                et être guidé à nouveau vers le paramétrage de votre structure.
              </p>
              <Button
                variant="outline"
                icon={<FiRefreshCw size={16} />}
                onClick={() => setShowResetModal(true)}
              >
                Réinitialiser l&apos;onboarding
              </Button>
            </div>
          </div>
        </Card>
        )}

        {isRhUser(user) && (
          <Card padding="1.5rem">
            <div style={{ display: 'flex', gap: '0.875rem', alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 'var(--radius-lg)',
                  background: 'var(--color-surface-hover)',
                  color: 'var(--color-text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FiSettings size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 0.35rem', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
                  Espace RH
                </h3>
                <p style={{ margin: '0 0 1rem', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                  Vous disposez d&apos;un accès RH complet : gestion des collaborateurs, structure, obligations et documents.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>

      <Modal
        isOpen={showResetModal}
        onClose={() => !resetting && setShowResetModal(false)}
        title="Réinitialiser l'introduction ?"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="ghost" onClick={() => setShowResetModal(false)} disabled={resetting}>
              Annuler
            </Button>
            <Button loading={resetting} onClick={handleResetOnboarding}>
              Confirmer
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
          Vous serez redirigé vers le parcours d&apos;introduction en 4 étapes. Vos données et paramétrages
          existants ne seront pas supprimés.
        </p>
      </Modal>
    </div>
  );
};

export default UserSettingsPage;
